import { create } from "zustand";
import { SimpleMeeting } from "../interfaces/Meeting";
import { SimplePipeline } from "../interfaces/Pipeline";
import { SimpleMerchant } from "../interfaces/Merchant";
import { User } from "../interfaces/User";
import { persist } from "zustand/middleware";

interface TemplateSelections {
    valueSelectCustomFieldPipeline: Record<string, SimplePipeline[]>;
    valueSelectCustomFieldMeeting: Record<string, SimpleMeeting[]>;
    valueSelectCustomFieldMerchant: Record<string, SimpleMerchant[]>;
    valueSelectCustomFieldUser: Record<string, User[]>;
    valueSelectMerchants: SimpleMerchant[];
}

interface ExcelStore {
    templates: Record<string, TemplateSelections>;
    updateTemplateSelections: (templateId: string, customFieldId: string, data: {
        valueSelectCustomFieldPipeline?: SimplePipeline[];
        valueSelectCustomFieldMeeting?: SimpleMeeting[];
        valueSelectCustomFieldMerchant?: SimpleMerchant[];
        valueSelectCustomFieldUser?: User[];
        valueSelectMerchants?: SimpleMerchant[];
    }) => void;
    saveSelectionsToLocalStorage: () => void;
    clearSelections: () => void;
    getTemplateSelections: (templateId: string) => TemplateSelections | undefined;
}

const getInitialState = (): Record<string, TemplateSelections> => {
    try {
        const savedData = localStorage.getItem('customFieldSelections');
        if (savedData) {
            const parsed = JSON.parse(savedData);
            return parsed.templates || {};
        }
    } catch (error) {
        console.error('Error loading data from localStorage:', error);
    }
    return {};
};

const initialState = getInitialState();

const useExcelStore = create<ExcelStore>()(
    persist(
        (set, get) => ({
            templates: initialState,
            updateTemplateSelections: (templateId: string, customFieldId: string, data) =>
                set((state) => {
                    const existingTemplateData = state.templates[templateId] || {
                        valueSelectCustomFieldPipeline: {},
                        valueSelectCustomFieldMeeting: {},
                        valueSelectCustomFieldMerchant: {},
                        valueSelectCustomFieldUser: {},
                        valueSelectMerchants: []
                    };

                    const updatedTemplate: TemplateSelections = {
                        ...existingTemplateData,
                    };

                    if (data.valueSelectCustomFieldPipeline !== undefined) {
                        updatedTemplate.valueSelectCustomFieldPipeline = {
                            ...existingTemplateData.valueSelectCustomFieldPipeline,
                            [customFieldId]: data.valueSelectCustomFieldPipeline
                        };
                    }

                    if (data.valueSelectCustomFieldMeeting !== undefined) {
                        updatedTemplate.valueSelectCustomFieldMeeting = {
                            ...existingTemplateData.valueSelectCustomFieldMeeting,
                            [customFieldId]: data.valueSelectCustomFieldMeeting
                        };
                    }

                    if (data.valueSelectCustomFieldMerchant !== undefined) {
                        updatedTemplate.valueSelectCustomFieldMerchant = {
                            ...existingTemplateData.valueSelectCustomFieldMerchant,
                            [customFieldId]: data.valueSelectCustomFieldMerchant
                        };
                    }

                    if (data.valueSelectCustomFieldUser !== undefined) {
                        updatedTemplate.valueSelectCustomFieldUser = {
                            ...existingTemplateData.valueSelectCustomFieldUser,
                            [customFieldId]: data.valueSelectCustomFieldUser
                        };
                    }

                    if (data.valueSelectMerchants !== undefined && customFieldId === 'originalMerchant') {
                        updatedTemplate.valueSelectMerchants = data.valueSelectMerchants;
                    }

                    return {
                        ...state,
                        templates: {
                            ...state.templates,
                            [templateId]: updatedTemplate
                        }
                    };
                }),

            saveSelectionsToLocalStorage: () => {
                const state = get();
                localStorage.setItem('customFieldSelections', JSON.stringify({
                    templates: state.templates
                }));
            },

            clearSelections: () =>
                set((state) => ({
                    ...state,
                    templates: {}
                })),

            getTemplateSelections: (templateId: string) => {
                const state = get();
                return state.templates[templateId];
            }
        }),
        {
            name: 'excel-storage',
            getStorage: () => localStorage,
        }
    )
);

export default useExcelStore;
